import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  useMediaQuery,
  IconButton,
  Collapse
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-GB';
import { fetchHistoricalTradesList } from '../../services/backendAPI';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { fetchTradeHistory } from '../../store/actions/orders';
import { formatTimeToIST } from '../../utils/dateUtils';
import ExportTradeHistoryModal from './ExportTradeHistoryModal';
import CollapsibleMobileCard from '../Common/CollapsibleMobileCard';

const TradeHistoryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  // Function to check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };
  
  // Function to get the most recent business day
  const getMostRecentBusinessDay = () => {
    const today = new Date();
    let recentBusinessDay = new Date(today);
    
    // If today is Monday, set to previous Friday
    if (today.getDay() === 1) { // Monday
      recentBusinessDay.setDate(today.getDate() - 3);
      return recentBusinessDay;
    }
    
    // If today is Sunday, set to previous Friday
    if (today.getDay() === 0) { // Sunday
      recentBusinessDay.setDate(today.getDate() - 2);
      return recentBusinessDay;
    }
    
    // If today is Saturday, set to previous Friday
    if (today.getDay() === 6) { // Saturday
      recentBusinessDay.setDate(today.getDate() - 1);
      return recentBusinessDay;
    }
    
    // For regular weekdays, use yesterday
    recentBusinessDay.setDate(today.getDate() - 1);
    return recentBusinessDay;
  };
  
  // Table data state
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Helper to get date 7 days ago
  const getSevenDaysAgo = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return sevenDaysAgo;
  };

  // Filter states - default to 7 days before today for start date
  const [startDate, setStartDate] = useState(getSevenDaysAgo());
  const [endDate, setEndDate] = useState(new Date());
  const [exchange, setExchange] = useState('NFO');
  const [buySell, setBuySell] = useState('');
  const [isErrorTrade, setIsErrorTrade] = useState(false);
  const dispatch = useDispatch();
  
  // Export modal state
  const [openExportModal, setOpenExportModal] = useState(false);
  
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      return format(dateObj, 'dd-MM-yyyy');
    } catch (error) {
      console.error('Error formatting display date:', error);
      return '';
    }
  };
  
  const fetchTrades = async (newPage, newRowsPerPage, overrideParams = null) => {
    setLoading(true);
    
    // Clear previous data immediately when starting a new fetch
    setTrades([]);
    setTotalCount(0);
    
    try {
      const sDate = overrideParams?.startDate || startDate;
      const eDate = overrideParams?.endDate || endDate;

      const params = {
        page: newPage + 1,
        pageSize: newRowsPerPage,
        fetchAll: 'ALL',
        clientIds: localStorage.getItem("login_id"), // Add missing clientIds parameter
        exchange: overrideParams?.exchange || exchange,
        startBusinessDate: formatDate(sDate),
        endBusinessDate: formatDate(eDate),
        buySell: overrideParams?.buySell !== undefined ? (overrideParams.buySell === '2' ? undefined : overrideParams.buySell) : (buySell === '2' ? undefined : buySell),
        isErrorTrade: overrideParams?.isErrorTrade !== undefined ? overrideParams.isErrorTrade : isErrorTrade
      };
      
      console.log("Trade History API params:", params);
      const result = await dispatch(fetchTradeHistory(params));
      console.log("Component received result:", result);
      
      if (result && result.trades) {
        setTrades(result.trades);
        setTotalCount(result.total_count || 0);
      } else if (result && result.data && result.data.trades) {
        setTrades(result.data.trades);
        setTotalCount(result.data.total_count || 0);
      } else {
        // throw new Error('Failed to fetch trade history');
      }
    } catch (error) {
      console.error("Component error:", error);
      enqueueSnackbar(error.message || 'Error fetching trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTrades(page, rowsPerPage);
  }, []);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchTrades(newPage, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTrades(0, newRowsPerPage);
  };

  const fetchReset = async () => {
    setLoading(true);
    
    // Clear previous data immediately when starting a new fetch
    setTrades([]);
    setTotalCount(0);
    
    try {
      const params = {
        page: 0,
        pageSize: 50,
        fetchAll: 'ALL',
        clientIds: localStorage.getItem("login_id"), // Add missing clientIds parameter
        exchange: "NFO",
        startBusinessDate: formatDate(getSevenDaysAgo()),
        endBusinessDate: formatDate(new Date()),
        isErrorTrade: false
      };
      
      const result = await dispatch(fetchTradeHistory(params));
      console.log("Component received result:", result);
      
      if (result && result.trades) {
        setTrades(result.trades);
        setTotalCount(result.total_count || 0);
      } else if (result && result.data && result.data.trades) {
        setTrades(result.data.trades);
        setTotalCount(result.data.total_count || 0);
      } else {
        throw new Error('Failed to fetch trade history');
      }
    } catch (error) {
      console.error("Component error:", error);
      enqueueSnackbar(error.message || 'Error fetching trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  
  const handleApplyFilters = () => {
    setPage(0);
    fetchTrades(0, rowsPerPage);
  };
  
  const handleResetFilters = () => {
    setStartDate(getSevenDaysAgo());
    setEndDate(new Date());
    setExchange('NFO');
    setBuySell('2');
    setIsErrorTrade(false);
    setPage(0);
    
    // Reset and fetch with default values
    fetchReset();
  };
  
  const handleOpenExportModal = () => {
    setOpenExportModal(true);
  };
  
  const handleCloseExportModal = () => {
    setOpenExportModal(false);
  };
  
  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      height: '100%',
      px: { xs: 0, sm: 1 },
      pt: { xs: 1, sm: 2 },
      pb: { xs: 0, sm: 2 },
      position: 'relative',
    }}>
      {/* Export Button - keep it but move to filters section */}
      
      {/* Filters */}
      <Paper 
        elevation={isMobile ? 0 : undefined} 
        sx={{ 
          p: isMobile ? '8px 12px' : 2, 
          mb: isMobile ? 1 : 1, 
          flexShrink: 0,
          bgcolor: isMobile ? 'transparent' : 'background.paper',
          boxShadow: isMobile ? 'none' : undefined,
          backgroundImage: isMobile ? 'none' : undefined
        }}
      >
        {isMobile ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: showMobileFilters ? 2 : 0
          }}>
            <Button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              startIcon={<FilterListIcon />}
              endIcon={showMobileFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              size="small"
              sx={{ 
                color: 'text.secondary',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                }
              }}
            >
              Filter
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleOpenExportModal}
              size="small"
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Export
            </Button>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography variant="h6">Filters</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleOpenExportModal}
              size="medium"
            >
              Export
            </Button>
          </Box>
        )}
        
        <Collapse in={!isMobile || showMobileFilters}>
          <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  setPage(0);
                  fetchTrades(0, rowsPerPage, { startDate: newValue });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                    InputLabelProps={{
                      style: { fontWeight: 600 }
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                maxDate={endDate || new Date()}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  setPage(0);
                  fetchTrades(0, rowsPerPage, { endDate: newValue });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                    InputLabelProps={{
                      style: { fontWeight: 600 }
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                minDate={startDate}
                maxDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.5}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="exchange-select-label">Exchange</InputLabel>
              <Select
                labelId="exchange-select-label"
                value={exchange}
                label="Exchange"
                onChange={(e) => {
                  const newExchange = e.target.value;
                  setExchange(newExchange);
                  // Auto-search when exchange is changed
                  setPage(0);
                  fetchTrades(0, rowsPerPage, { exchange: newExchange });
                }}
              >
                <MenuItem value="NSE">Cash NSE</MenuItem>
                <MenuItem value="BSE">Cash BSE</MenuItem>
                <MenuItem value="NFO">Derivative NFO</MenuItem>
                <MenuItem value="BFO">Derivative BFO</MenuItem>
                <MenuItem value="MCX">Commodities</MenuItem>
                <MenuItem value="CDS">Forex</MenuItem>
                <MenuItem value="DCSX">Crypto</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.5}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="buysell-select-label">Buy/Sell</InputLabel>
              <Select
                labelId="buysell-select-label"
                value={buySell}
                label="Buy/Sell"
                onChange={(e) => {
                  const newBuySell = e.target.value;
                  setBuySell(newBuySell);
                  // Auto-search when buy/sell filter is changed
                  setPage(0);
                  fetchTrades(0, rowsPerPage, { buySell: newBuySell });
                }}
              >
                <MenuItem value="2">All</MenuItem>
                <MenuItem value="0">Buy</MenuItem>
                <MenuItem value="1">Sell</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4} md={1.5}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="error-trade-select-label">Error Trades</InputLabel>
              <Select
                labelId="error-trade-select-label"
                value={isErrorTrade}
                label="Error Trades"
                onChange={(e) => {
                  const newIsErrorTrade = e.target.value;
                  setIsErrorTrade(newIsErrorTrade);
                  // Auto-search when error trades filter is changed
                  setPage(0);
                  fetchTrades(0, rowsPerPage, { isErrorTrade: newIsErrorTrade });
                }}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%'
            }}>
              <Button 
                variant="contained" 
                startIcon={<SearchIcon />} 
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={handleResetFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Reset
              </Button>
            </Box>
          </Grid>
          </Grid>
        </Collapse>
      </Paper>
      
      {/* Results */}
      <Paper sx={{ 
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        width: '100%'
      }}>
        <TableContainer sx={{ 
          flex: 1,
          overflow: 'auto',
            overflowX: 'auto',
            minHeight: 0,
            willChange: 'scroll-position',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            '&::-webkit-scrollbar': {
              height: '8px',
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.grey[100],
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : trades.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              p: 6,
              textAlign: 'center'
            }}>
              <SearchIcon sx={{ 
                fontSize: 64, 
                color: theme.palette.grey[400],
                mb: 2
              }} />
              <Typography variant="h6" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                No Trade History Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
              No trades found for the selected date.
              </Typography>
            </Box>
          ) : (
            <>
              {isMobile ? (
                // Mobile view - Card layout
                <Box sx={{ 
                  p: 1,
                  willChange: 'scroll-position',
                  WebkitOverflowScrolling: 'touch',
                }}>
                  {trades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((trade) => (
                    <CollapsibleMobileCard
                      key={trade.trade_id || trade.id}
                      headerLeft={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {trade.trading_symbol}
                        </Typography>
                      }
                      headerBadge={
                        <Chip 
                          label={trade.buy_sell === 0 ? "Buy" : "Sell"} 
                          color={trade.buy_sell === 0 ? "success" : "error"}
                          size="small"
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem', 
                            fontWeight: 600,
                            borderRadius: 1
                          }}
                        />
                      }
                      headerRight={
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          ₹{trade.fill_price}
                        </Typography>
                      }
                    >
                      <Grid container spacing={2}>
                        {/* Row 1 */}
                        <Grid item xs={7}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Order ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {trade.order_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Quantity
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {trade.fill_quantity}
                          </Typography>
                        </Grid>

                        {/* Row 2 */}
                        <Grid item xs={7}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Trade ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {trade.trade_id || trade.id}
                          </Typography>
                        </Grid>
                        <Grid item xs={5}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Trade Price
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            ₹{trade.fill_price}
                          </Typography>
                        </Grid>

                        {/* Row 3 */}
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Value
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            ₹{(trade.fill_quantity * trade.fill_price)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {trade.exchange_time ? formatDisplayDate(trade.exchange_time * 1000) : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Time
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {formatTimeToIST(trade.exchange_time)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Chip 
                            label={trade.is_error_trade ? "ERROR" : "SUCCESS"} 
                            color={trade.is_error_trade ? "error" : "success"}
                            size="small"
                            sx={{ fontWeight: 600, borderRadius: 1 }}
                          />
                        </Grid>
                      </Grid>
                    </CollapsibleMobileCard>
                  ))}
                </Box>
              ) : (
                // Desktop view - Table layout
                <Table 
                  stickyHeader 
                  aria-label="sticky table" 
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    tableLayout: 'auto',
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Trade ID</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Trade Time</TableCell>
                      <TableCell>Buy/Sell</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Trade Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trades.map((trade) => (
                      <TableRow hover role="checkbox" tabIndex={-1} key={trade.trade_id || trade.id}>
                        <TableCell>{trade.order_id}</TableCell>
                        <TableCell>{trade.trade_id || trade.id}</TableCell>
                        <TableCell>{trade.trading_symbol}</TableCell>
                        <TableCell>{formatTimeToIST(trade.exchange_time)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={trade.buy_sell === 0 ? "Buy" : "Sell"} 
                            color={trade.buy_sell === 0 ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{trade.fill_quantity}</TableCell>
                        <TableCell>{trade.fill_price}</TableCell>
                        <TableCell>
                          <Chip 
                            label={trade.is_error_trade ? "ERROR" : "SUCCESS"} 
                            color={trade.is_error_trade ? "error" : "success"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </TableContainer>
      </Paper>
      
      {trades.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
          sx={{
            flexShrink: 0,
            width: '100%',
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            '& .MuiToolbar-root': {
              minHeight: '40px',
              px: 1
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              margin: 0,
              fontSize: '0.8rem'
            }
          }}
        />
      )}
      
      {/* Export Modal */}
      <ExportTradeHistoryModal
        open={openExportModal}
        onClose={handleCloseExportModal}
        defaultExchange={exchange}
        title="Export Trade History"
      />
    </Box>
  );
};

export default TradeHistoryDetails; 